/* ============================================================
   Formatting helpers

   Centralised so that every figure in the product is rendered the same
   way — and so the "0" vs "—" distinction (a real value of zero vs. no
   value at all) is made once rather than per component.
   ============================================================ */

const EN = 'en-GB';

export function money(value, currency = 'USD', decimals = 0) {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(EN, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
}

export function number(value, decimals = 0) {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(EN, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
}

export function percent(value, decimals = 0) {
  if (!Number.isFinite(value)) return '—';
  return `${number(value, decimals)}%`;
}

export function signedPercent(value, decimals = 1) {
  if (!Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${number(value, decimals)}%`;
}

export function longDate(input) {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(EN, {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(date);
}

export function shortDate(input) {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(EN, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(date);
}

/** Whole days from today to `input`. Negative when in the past. */
export function daysUntil(input, now = new Date()) {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return null;
  const a = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const b = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((a - b) / 86400000);
}

function plural(count, unit) {
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

export function relativeDays(days) {
  if (days === null) return '—';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';

  const past = Math.abs(days);
  const magnitude =
    past < 31
      ? plural(past, 'day')
      : past < 365
        ? plural(Math.max(1, Math.round(past / 30)), 'month')
        : plural(Math.max(1, Math.round(past / 365)), 'year');

  return days > 0 ? `in ${magnitude}` : `${magnitude} ago`;
}

/** Parse a numeric form field. Returns null for blank/invalid rather than 0. */
export function parseNumber(input) {
  if (input === null || input === undefined) return null;
  const trimmed = String(input).trim();
  if (trimmed === '') return null;
  const value = Number(trimmed.replace(/,/g, ''));
  return Number.isFinite(value) ? value : null;
}

export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
